import React, { useMemo, useState } from 'react';
import { PencilLine, Plus, Search, UserX } from 'lucide-react';
import {
  AMPEL_NACHWEIS,
  LEERER_PERSONEN_STAND,
  formatDatum,
  personenAmpel,
  standJePerson,
  sucheMitarbeiter,
} from '../../../services/sifa/kundenportalLogic';
import type { KundenportalDaten, PortalMitarbeiter, PortalMitarbeiterEingabe } from '../../../services/sifa/kundenportalTypes';
import { EINGABE, HINWEISKASTEN, KNOPF_PRIMAER } from '../public/teilnahmeSeiteStil';
import { PortalMitarbeiterDialog } from './PortalMitarbeiterDialog';

/**
 * Mitarbeiterstamm des Betriebs: Liste mit Nachweis-Ampel, Suche, Anlegen/
 * Bearbeiten im Dialog, Deaktivieren (aktiv = false, kein Loeschen).
 */
export const MitarbeiterTab: React.FC<{
  daten: KundenportalDaten;
  onSpeichern: (eingabe: PortalMitarbeiterEingabe) => Promise<{ ok: boolean; text: string | null }>;
}> = ({ daten, onSpeichern }) => {
  const [suche, setSuche] = useState('');
  const [inaktiveZeigen, setInaktiveZeigen] = useState(false);
  const [dialog, setDialog] = useState<{ offen: boolean; person: PortalMitarbeiter | null }>({ offen: false, person: null });
  const [deaktiviertId, setDeaktiviertId] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const stand = useMemo(() => standJePerson(daten.teilnahmen), [daten.teilnahmen]);
  const liste = useMemo(
    () => sucheMitarbeiter(daten.mitarbeiter.filter((m) => inaktiveZeigen || m.aktiv), suche),
    [daten.mitarbeiter, inaktiveZeigen, suche],
  );
  const inaktive = daten.mitarbeiter.filter((m) => !m.aktiv).length;

  const deaktivieren = async (m: PortalMitarbeiter) => {
    if (!window.confirm(`${m.vorname} ${m.nachname} als ausgeschieden markieren? Die Person wird deaktiviert, ihre Nachweise bleiben erhalten.`)) return;
    setDeaktiviertId(m.id);
    setFehler(null);
    const ergebnis = await onSpeichern({
      id: m.id,
      vorname: m.vorname,
      nachname: m.nachname,
      geburtsdatum: m.geburtsdatum,
      email: m.email,
      abteilung: m.abteilung,
      personalnummer: m.personalnummer,
      aktiv: false,
    });
    setDeaktiviertId(null);
    if (!ergebnis.ok) setFehler(ergebnis.text);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Suchen (Name, Abteilung, E-Mail, Personalnummer)"
            className={`${EINGABE} pl-9`}
            aria-label="Mitarbeiter suchen"
          />
        </div>
        <button type="button" onClick={() => setDialog({ offen: true, person: null })} className={`${KNOPF_PRIMAER} flex items-center justify-center gap-2`}>
          <Plus className="w-5 h-5" /> Anlegen
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 text-sm text-slate-500">
        <span>
          {liste.length} {liste.length === 1 ? 'Person' : 'Personen'}
          {inaktive > 0 && !inaktiveZeigen ? ` · ${inaktive} inaktiv ausgeblendet` : ''}
        </span>
        {inaktive > 0 && (
          <label className="flex items-center gap-2 min-h-[36px] cursor-pointer">
            <input type="checkbox" checked={inaktiveZeigen} onChange={(e) => setInaktiveZeigen(e.target.checked)} className="w-4 h-4 rounded" />
            Inaktive anzeigen
          </label>
        )}
      </div>

      {fehler && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{fehler}</div>}

      {liste.length === 0 ? (
        <div className={HINWEISKASTEN}>
          {daten.mitarbeiter.length === 0
            ? 'Noch keine Mitarbeiter angelegt. Mit „Anlegen" beginnen — oder die Mitarbeiter führen die erste Unterweisung über den Link durch, dann entstehen sie von selbst.'
            : 'Keine Person passt zur Suche.'}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
          {liste.map((m) => {
            const s = stand[m.id] || LEERER_PERSONEN_STAND;
            const stufe = personenAmpel(s);
            const ampel = AMPEL_NACHWEIS[stufe];
            return (
              <li key={m.id} className={`p-3 flex items-start gap-3 ${m.aktiv ? '' : 'bg-slate-50 opacity-70'}`}>
                <span className={`mt-1.5 w-3 h-3 rounded-full shrink-0 ${ampel.punkt}`} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">
                    {m.nachname}, {m.vorname}
                    {!m.aktiv && <span className="ml-2 text-xs font-bold text-slate-500 uppercase">inaktiv</span>}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {[m.abteilung, m.personalnummer ? `Nr. ${m.personalnummer}` : null, m.email].filter(Boolean).join(' · ') || '—'}
                  </p>
                  <p className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-[11px] font-bold border rounded-full ${ampel.klasse}`}>
                    {ampel.label}
                    {s.gueltigBis && stufe !== 'ohne' ? ` · bis ${formatDatum(s.gueltigBis)}` : ''}
                    {stufe === 'gueltig' && !s.gueltigBis ? ' · ohne Ablauf' : ''}
                  </p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => setDialog({ offen: true, person: m })}
                    className="min-h-[40px] px-3 flex items-center gap-1.5 text-xs font-semibold rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100"
                  >
                    <PencilLine className="w-4 h-4" /> Bearbeiten
                  </button>
                  {m.aktiv && (
                    <button
                      type="button"
                      onClick={() => void deaktivieren(m)}
                      disabled={deaktiviertId === m.id}
                      className="min-h-[40px] px-3 flex items-center gap-1.5 text-xs font-semibold rounded-lg text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
                    >
                      <UserX className="w-4 h-4" /> Deaktivieren
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {dialog.offen && (
        <PortalMitarbeiterDialog mitarbeiter={dialog.person} onClose={() => setDialog({ offen: false, person: null })} onSpeichern={onSpeichern} />
      )}
    </div>
  );
};
